// SBIS Consulting — Balance Sheet + Inventory izveštaji
import { state } from './state.js';
import { sb } from './supabase.js';
import { fNum } from './utils.js';
import { toast, showLoading, hideLoading } from './ui.js';
import XLSX from 'xlsx-js-style';

let lastBsData = [];
let lastBsKurs = 108;
let lastBsMonth = 0;
let lastBsYear = 0;

const MONTHS_SR = ['Januar','Februar','Mart','April','Maj','Jun','Jul','Avgust','Septembar','Oktobar','Novembar','Decembar'];

// Class groupings for BS display
const BS_CLASSES = [
  { prefix: '0', label: 'UPISANI KAPITAL I STALNA IMOVINA' },
  { prefix: '1', label: 'ZALIHE I SREDSTVA NAMENJENA PRODAJI' },
  { prefix: '2', label: 'KRATKOROČNA POTRAŽIVANJA, PLASMANI I GOTOVINA' },
  { prefix: '3', label: 'KAPITAL' },
  { prefix: '4', label: 'OBAVEZE' },
  { prefix: '5', label: 'RASHODI' },
  { prefix: '6', label: 'PRIHODI' },
  { prefix: '7', label: 'OTVARANJE I ZAKLJUČAK' }
];


export async function generateBS(mode) {
  if (!state.CU) return;
  const selId = { bs: 'bsMonth', inv: 'invMonth', pl: 'plMonth' }[mode] || 'bsMonth';
  const yearId = { bs: 'bsYear', inv: 'invYear', pl: 'plYear' }[mode] || 'bsYear';
  const kursId = { bs: 'bsKurs', inv: 'invKurs', pl: 'plKurs' }[mode] || 'bsKurs';
  const month = parseInt(document.getElementById(selId).value);
  const year = parseInt(document.getElementById(yearId).value);
  const kurs = parseFloat(document.getElementById(kursId).value) || 108;

  showLoading();
  try {
    const { data, error } = await sb.rpc('calculate_balance_sheet', {
      p_user_id: state.CU.id, p_year: year, p_month: month, p_kurs: kurs
    });
    if (error) throw error;
    lastBsData = data || [];
    lastBsKurs = kurs;
    lastBsMonth = month;
    lastBsYear = year;
    if (mode === 'inv') renderInventory(lastBsData, kurs, month, year);
    else if (mode === 'pl') renderPL(lastBsData, kurs, month, year);
    else renderBS(lastBsData, kurs, month, year);
    const labels = { bs: 'Balance Sheet', inv: 'Inventory', pl: 'P&L' };
    toast((labels[mode] || 'Izveštaj') + ' generisan za ' + MONTHS_SR[month - 1] + ' ' + year);
  } catch (e) { toast('Greška: ' + (e.message || e), 'err'); }
  hideLoading();
}

function renderBS(data, kurs, month, year) {
  const out = document.getElementById('bsOutput'); out.textContent = '';
  if (!data.length) { out.textContent = 'Nema podataka.'; return; }

  document.getElementById('bsExpBtn').style.display = '';

  // Summary KPI
  const totalD = data.reduce((s, r) => s + parseFloat(r.duguje || 0), 0);
  const totalC = data.reduce((s, r) => s + parseFloat(r.potrazuje || 0), 0);
  const bar = document.createElement('div'); bar.style.cssText = 'display:flex;gap:14px;margin-bottom:16px;flex-wrap:wrap';
  [{ l: 'Konta', v: data.length, c: 'var(--acc)' }, { l: 'Ukupno Duguje', v: fNum(totalD), c: 'var(--dng)' }, { l: 'Ukupno Potražuje', v: fNum(totalC), c: 'var(--suc)' }, { l: 'Kurs', v: fNum(kurs), c: 'var(--tx)' }].forEach(s => {
    const card = document.createElement('div'); card.className = 'card'; card.style.cssText = 'padding:14px 22px;flex:1;min-width:180px';
    const lbl = document.createElement('div'); lbl.style.cssText = 'font-size:14px;color:var(--tx3);text-transform:uppercase;letter-spacing:.5px;margin-bottom:4px'; lbl.textContent = s.l;
    const val = document.createElement('div'); val.style.cssText = 'font-size:26px;font-weight:700;font-family:var(--mn);color:' + s.c; val.textContent = s.v;
    card.appendChild(lbl); card.appendChild(val); bar.appendChild(card);
  });
  out.appendChild(bar);

  // Group by class
  BS_CLASSES.forEach(cls => {
    const items = data.filter(r => r.konto.startsWith(cls.prefix));
    if (!items.length) return;
    const classD = items.reduce((s, r) => s + parseFloat(r.duguje || 0), 0);
    const classC = items.reduce((s, r) => s + parseFloat(r.potrazuje || 0), 0);
    const classSaldo = classD - classC;

    const card = document.createElement('div'); card.className = 'card'; card.style.cssText = 'padding:0;margin-bottom:12px;overflow:hidden';
    // Header as table matching column widths
    const hdr = document.createElement('div'); hdr.style.cssText = 'cursor:pointer;background:var(--surface)';
    const ht = document.createElement('table'); ht.style.cssText = 'width:100%;table-layout:fixed;border-collapse:collapse';
    const hcg = document.createElement('colgroup');
    [{w:'7%'},{w:'25%'},{w:'15%'},{w:'15%'},{w:'17%'},{w:'21%'}].forEach(c => { const col = document.createElement('col'); col.style.width = c.w; hcg.appendChild(col); });
    ht.appendChild(hcg);
    const htr2 = document.createElement('tr');
    const htd1 = document.createElement('td'); htd1.colSpan = 2; htd1.style.cssText = 'padding:10px 16px;font-weight:700;font-size:17px;color:var(--acc)'; htd1.textContent = 'Klasa ' + cls.prefix + ' \u2014 ' + cls.label;
    const htd3 = document.createElement('td'); htd3.style.cssText = 'padding:10px 16px;text-align:right;font-family:var(--mn);font-size:17px;font-weight:700;color:var(--dng);border-right:1px solid var(--border-lt)'; htd3.textContent = fNum(classD);
    const htd4 = document.createElement('td'); htd4.style.cssText = 'padding:10px 16px;text-align:right;font-family:var(--mn);font-size:17px;font-weight:700;color:var(--suc);border-right:1px solid var(--border-lt)'; htd4.textContent = fNum(classC);
    const htd5 = document.createElement('td'); htd5.style.cssText = 'padding:10px 16px;text-align:right;font-family:var(--mn);font-size:17px;font-weight:700;color:' + (classSaldo >= 0 ? 'var(--dng)' : 'var(--suc)'); htd5.textContent = fNum(classSaldo);
    const htd6 = document.createElement('td'); htd6.style.cssText = 'padding:10px 8px;text-align:center';
    const ch = document.createElement('span'); ch.style.cssText = 'color:var(--tx3);font-size:13px;transition:transform .2s'; ch.textContent = '\u25BC';
    htd6.appendChild(ch);
    htr2.appendChild(htd1); htr2.appendChild(htd3); htr2.appendChild(htd4); htr2.appendChild(htd5); htr2.appendChild(htd6);
    ht.appendChild(htr2); hdr.appendChild(ht);

    const body = document.createElement('div'); body.style.display = 'none';
    hdr.onclick = () => { const o = body.style.display !== 'none'; body.style.display = o ? 'none' : 'block'; ch.style.transform = o ? '' : 'rotate(180deg)'; };

    // Column headers
    const tbl = document.createElement('table'); tbl.style.cssText = 'width:100%;font-size:17px;border-collapse:collapse';
    // Column widths
    const cg = document.createElement('colgroup');
    [{w:'7%'},{w:'25%'},{w:'15%'},{w:'15%'},{w:'17%'},{w:'21%'}].forEach(c => { const col = document.createElement('col'); col.style.width = c.w; cg.appendChild(col); });
    tbl.appendChild(cg);
    const thead = document.createElement('thead'); const htr = document.createElement('tr'); htr.style.cssText = 'background:var(--raised);border-bottom:2px solid var(--border)';
    ['Šifra','Naziv','Duguje','Potražuje','Saldo','Saldo USD'].forEach((h,i) => {
      const th = document.createElement('th'); th.textContent = h;
      th.style.cssText = 'padding:12px 16px;font-size:14px;color:var(--tx3);text-transform:uppercase;letter-spacing:.5px;text-align:' + (i >= 2 ? 'right' : 'left');
      htr.appendChild(th);
    });
    thead.appendChild(htr); tbl.appendChild(thead);

    items.sort((a, b) => a.konto.localeCompare(b.konto)).forEach(r => {
      const d = parseFloat(r.duguje || 0), c = parseFloat(r.potrazuje || 0), saldo = parseFloat(r.saldo || 0);
      const tr = document.createElement('tr'); tr.style.borderBottom = '1px solid var(--border-lt)';
      const td1 = document.createElement('td'); td1.style.cssText = 'padding:10px 14px;font-family:var(--mn);font-weight:700;color:var(--acc);font-size:17px'; td1.textContent = r.konto;
      const td2 = document.createElement('td'); td2.style.cssText = 'padding:10px 14px;color:var(--tx);font-size:16px'; td2.textContent = r.naziv || '';
      const td3 = document.createElement('td'); td3.style.cssText = 'padding:10px 14px;text-align:right;font-family:var(--mn);font-size:17px;font-weight:600'; if (d > 0) { td3.textContent = fNum(d); td3.style.color = 'var(--dng)'; }
      const td4 = document.createElement('td'); td4.style.cssText = 'padding:10px 14px;text-align:right;font-family:var(--mn);font-size:17px;font-weight:600'; if (c > 0) { td4.textContent = fNum(c); td4.style.color = 'var(--suc)'; }
      const td5 = document.createElement('td'); td5.style.cssText = 'padding:10px 14px;text-align:right;font-family:var(--mn);font-weight:700;font-size:17px;color:' + (saldo >= 0 ? 'var(--dng)' : 'var(--suc)'); td5.textContent = fNum(saldo);
      const td6 = document.createElement('td'); td6.style.cssText = 'padding:10px 14px;text-align:right;font-family:var(--mn);font-size:17px;color:var(--tx3)'; td6.textContent = fNum(parseFloat(r.saldo_usd || 0));
      tr.appendChild(td1); tr.appendChild(td2); tr.appendChild(td3); tr.appendChild(td4); tr.appendChild(td5); tr.appendChild(td6);
      tbl.appendChild(tr);
    });
    // Class total row
    const ttr = document.createElement('tr'); ttr.style.cssText = 'background:var(--surface);font-weight:700;border-top:2px solid var(--acc)';
    const tt1 = document.createElement('td'); tt1.colSpan = 2; tt1.style.cssText = 'padding:12px 14px;font-size:17px;color:var(--acc)'; tt1.textContent = 'UKUPNO Klasa ' + cls.prefix;
    const tt3 = document.createElement('td'); tt3.style.cssText = 'padding:12px 14px;text-align:right;font-family:var(--mn);font-size:19px;color:var(--dng)'; tt3.textContent = fNum(classD);
    const tt4 = document.createElement('td'); tt4.style.cssText = 'padding:12px 14px;text-align:right;font-family:var(--mn);font-size:19px;color:var(--suc)'; tt4.textContent = fNum(classC);
    const tt5 = document.createElement('td'); tt5.style.cssText = 'padding:12px 14px;text-align:right;font-family:var(--mn);font-size:19px;font-weight:700;color:' + (classSaldo >= 0 ? 'var(--dng)' : 'var(--suc)'); tt5.textContent = fNum(classSaldo);
    const tt6 = document.createElement('td'); tt6.style.cssText = 'padding:12px 14px;text-align:right;font-family:var(--mn);font-size:19px;color:var(--tx3)'; tt6.textContent = fNum(classSaldo / kurs);
    ttr.appendChild(tt1); ttr.appendChild(tt3); ttr.appendChild(tt4); ttr.appendChild(tt5); ttr.appendChild(tt6);
    tbl.appendChild(ttr);
    body.appendChild(tbl); card.appendChild(hdr); card.appendChild(body); out.appendChild(card);
  });
}

function renderInventory(data, kurs, month, year) {
  const out = document.getElementById('invOutput'); out.textContent = '';
  const invData = data.filter(r => r.konto.startsWith('1'));
  if (!invData.length) { out.textContent = 'Nema podataka za zalihe.'; return; }

  document.getElementById('invExpBtn').style.display = '';

  const totalSaldo = invData.reduce((s, r) => s + parseFloat(r.saldo || 0), 0);

  // Summary
  const sumDiv = document.createElement('div'); sumDiv.className = 'card'; sumDiv.style.cssText = 'padding:10px 14px;margin-bottom:16px;display:flex;justify-content:space-between;align-items:center';
  const sumL = document.createElement('div'); sumL.style.cssText = 'font-size:16px;color:var(--tx2)'; sumL.textContent = 'Ukupne zalihe \u2014 ' + MONTHS_SR[month - 1] + ' ' + year;
  const sumR = document.createElement('div'); sumR.style.cssText = 'font-family:var(--mn);font-size:26px;font-weight:700;color:var(--acc)'; sumR.textContent = fNum(totalSaldo) + ' RSD';
  const sumU = document.createElement('div'); sumU.style.cssText = 'font-family:var(--mn);font-size:17px;color:var(--tx3);margin-top:2px'; sumU.textContent = fNum(totalSaldo / kurs) + ' USD';
  const sumRW = document.createElement('div'); sumRW.style.textAlign = 'right'; sumRW.appendChild(sumR); sumRW.appendChild(sumU);
  sumDiv.appendChild(sumL); sumDiv.appendChild(sumRW); out.appendChild(sumDiv);

  // Table
  const card = document.createElement('div'); card.className = 'card'; card.style.cssText = 'padding:0;overflow:hidden';
  const tbl = document.createElement('table'); tbl.style.cssText = 'width:100%;font-size:17px;border-collapse:collapse';
  const cgI = document.createElement('colgroup');
  [{w:'10%'},{w:'50%'},{w:'20%'},{w:'20%'}].forEach(c => { const col = document.createElement('col'); col.style.width = c.w; cgI.appendChild(col); });
  tbl.appendChild(cgI);
  const thead = document.createElement('thead'); const htr = document.createElement('tr'); htr.style.cssText = 'background:var(--raised)';
  ['Konto', 'Naziv', 'Saldo RSD', 'Saldo USD'].forEach(h => {
    const th = document.createElement('th'); th.textContent = h;
    th.style.cssText = 'text-align:left;padding:12px 16px;border-bottom:2px solid var(--border);color:var(--tx3);font-size:13px;text-transform:uppercase;letter-spacing:.5px';
    if (h.includes('Saldo')) th.style.textAlign = 'right';
    htr.appendChild(th);
  });
  thead.appendChild(htr); tbl.appendChild(thead);

  const tbody = document.createElement('tbody');
  invData.sort((a, b) => a.konto.localeCompare(b.konto)).forEach(r => {
    const saldo = parseFloat(r.saldo || 0);
    if (Math.abs(saldo) < 0.01) return;
    const tr = document.createElement('tr'); tr.style.borderBottom = '1px solid var(--border-lt)';
    const td1 = document.createElement('td'); td1.style.cssText = 'padding:10px 14px;font-family:var(--mn);font-weight:700;color:var(--acc);font-size:17px'; td1.textContent = r.konto;
    const td2 = document.createElement('td'); td2.style.cssText = 'padding:10px 14px;color:var(--tx);font-size:16px'; td2.textContent = r.naziv || '';
    const td3 = document.createElement('td'); td3.style.cssText = 'padding:10px 14px;text-align:right;font-family:var(--mn);font-weight:700;font-size:17px;color:' + (saldo >= 0 ? 'var(--dng)' : 'var(--suc)'); td3.textContent = fNum(saldo);
    const td4 = document.createElement('td'); td4.style.cssText = 'padding:10px 14px;text-align:right;font-family:var(--mn);font-size:17px;color:var(--tx3)'; td4.textContent = fNum(saldo / kurs);
    tr.appendChild(td1); tr.appendChild(td2); tr.appendChild(td3); tr.appendChild(td4); tbody.appendChild(tr);
  });
  // Total
  const ttr = document.createElement('tr'); ttr.style.cssText = 'background:var(--surface);font-weight:700;border-top:2px solid var(--border)';
  const tt1 = document.createElement('td'); tt1.colSpan = 2; tt1.style.cssText = 'padding:12px 14px;font-size:17px'; tt1.textContent = 'UKUPNO ZALIHE';
  const tt3 = document.createElement('td'); tt3.style.cssText = 'padding:12px 14px;text-align:right;font-family:var(--mn);font-size:19px;color:var(--acc)'; tt3.textContent = fNum(totalSaldo);
  const tt4 = document.createElement('td'); tt4.style.cssText = 'padding:12px 14px;text-align:right;font-family:var(--mn);font-size:19px;color:var(--tx3)'; tt4.textContent = fNum(totalSaldo / kurs);
  ttr.appendChild(tt1); ttr.appendChild(tt3); ttr.appendChild(tt4); tbody.appendChild(ttr);
  tbl.appendChild(tbody); card.appendChild(tbl); out.appendChild(card);
}

export function exportBsXlsx() { exportSheet(lastBsData, lastBsKurs, lastBsMonth, lastBsYear, 'bs'); }
export function exportInvXlsx() { exportSheet(lastBsData.filter(r => r.konto.startsWith('1')), lastBsKurs, lastBsMonth, lastBsYear, 'inv'); }
export function exportPlXlsx() { exportSheet(lastBsData.filter(r => r.konto.startsWith('5') || r.konto.startsWith('6')), lastBsKurs, lastBsMonth, lastBsYear, 'pl'); }

// ============================================================
// P&L RENDERING
// ============================================================
const PL_SECTIONS = [
  { title: 'PRIHODI (Revenue)', groups: [
    { prefix: '60', label: 'Prihodi od prodaje proizvoda i usluga' },
    { prefix: '61', label: 'Prihodi od prodaje robe' },
    { prefix: '62', label: 'Prihodi od aktiviranja učinaka' },
    { prefix: '64', label: 'Prihodi od premija, subvencija, dotacija' },
    { prefix: '65', label: 'Finansijski prihodi' },
    { prefix: '66', label: 'Ostali prihodi' },
    { prefix: '67', label: 'Prihodi od usklađivanja vrednosti' },
    { prefix: '68', label: 'Prihodi od korigovanja' }
  ], sign: 1, color: 'var(--suc)' },
  { title: 'RASHODI (Expenses)', groups: [
    { prefix: '50', label: 'Nabavna vrednost prodate robe' },
    { prefix: '51', label: 'Troškovi materijala' },
    { prefix: '52', label: 'Troškovi zarada i naknada' },
    { prefix: '53', label: 'Troškovi amortizacije' },
    { prefix: '54', label: 'Ostali poslovni rashodi' },
    { prefix: '55', label: 'Finansijski rashodi' },
    { prefix: '56', label: 'Ostali rashodi' },
    { prefix: '57', label: 'Rashodi od usklađivanja vrednosti' },
    { prefix: '58', label: 'Rashodi od korigovanja' }
  ], sign: -1, color: 'var(--dng)' }
];

function renderPL(data, kurs, month, year) {
  const out = document.getElementById('plOutput'); out.textContent = '';
  const plData = data.filter(r => r.konto.startsWith('5') || r.konto.startsWith('6'));
  if (!plData.length) { out.textContent = 'Nema podataka za P&L.'; return; }

  document.getElementById('plExpBtn').style.display = '';

  let totalRevenue = 0, totalExpenses = 0;

  const plCols = [{w:'8%'},{w:'32%'},{w:'30%'},{w:'30%'}];
  function mkCg() { const cg = document.createElement('colgroup'); plCols.forEach(c => { const col = document.createElement('col'); col.style.width = c.w; cg.appendChild(col); }); return cg; }

  PL_SECTIONS.forEach(sec => {
    let sectionTotal = 0;
    const secCard = document.createElement('div'); secCard.className = 'card'; secCard.style.cssText = 'padding:0;margin-bottom:12px;overflow:hidden';

    const secHdr = document.createElement('div'); secHdr.style.cssText = 'padding:10px 14px;background:var(--surface);border-bottom:2px solid ' + sec.color;
    const secTitle = document.createElement('h3'); secTitle.style.cssText = 'font-size:19px;color:' + sec.color + ';margin:0'; secTitle.textContent = sec.title;
    secHdr.appendChild(secTitle); secCard.appendChild(secHdr);

    sec.groups.forEach(grp => {
      const items = plData.filter(r => r.konto.startsWith(grp.prefix));
      if (!items.length) return;
      const grpTotal = items.reduce((s, r) => s + parseFloat(r.saldo || 0), 0);
      sectionTotal += grpTotal;

      // Group header as table
      const ght = document.createElement('table'); ght.style.cssText = 'width:100%;table-layout:fixed;border-collapse:collapse;cursor:pointer;background:var(--raised);border-bottom:1px solid var(--border-lt)';
      ght.appendChild(mkCg());
      const ghtr = document.createElement('tr');
      const ght1 = document.createElement('td'); ght1.colSpan = 2; ght1.style.cssText = 'padding:12px 16px;font-size:17px;font-weight:600;color:var(--tx)'; ght1.textContent = grp.prefix + ' \u2014 ' + grp.label;
      const ght3 = document.createElement('td'); ght3.style.cssText = 'padding:12px 16px;text-align:right;font-family:var(--mn);font-size:17px;font-weight:700;color:' + sec.color; ght3.textContent = fNum(Math.abs(grpTotal));
      const ght4 = document.createElement('td'); ght4.style.cssText = 'padding:12px 16px;text-align:right;font-family:var(--mn);font-size:17px;color:var(--tx3)';
      ght4.textContent = fNum(Math.abs(grpTotal) / kurs) + ' USD ';
      const chS = document.createElement('span'); chS.style.cssText = 'color:var(--tx3);font-size:14px;transition:transform .2s'; chS.textContent = '\u25BC';
      ght4.appendChild(chS);
      ghtr.appendChild(ght1); ghtr.appendChild(ght3); ghtr.appendChild(ght4); ght.appendChild(ghtr);

      const body = document.createElement('div'); body.style.display = 'none';
      ght.onclick = () => { const o = body.style.display !== 'none'; body.style.display = o ? 'none' : 'block'; chS.style.transform = o ? '' : 'rotate(180deg)'; };

      // Detail table
      const tbl = document.createElement('table'); tbl.style.cssText = 'width:100%;table-layout:fixed;border-collapse:collapse';
      tbl.appendChild(mkCg());
      const thdr = document.createElement('tr'); thdr.style.cssText = 'background:var(--raised);border-bottom:1px solid var(--border)';
      ['Šifra','Naziv','Iznos RSD','Iznos USD'].forEach((h,i) => { const th = document.createElement('th'); th.textContent = h; th.style.cssText = 'padding:8px 16px;font-size:14px;color:var(--tx3);text-transform:uppercase;text-align:' + (i >= 2 ? 'right' : 'left'); thdr.appendChild(th); });
      tbl.appendChild(thdr);
      items.sort((a, b) => a.konto.localeCompare(b.konto)).forEach(r => {
        const saldo = parseFloat(r.saldo || 0);
        const tr = document.createElement('tr'); tr.style.borderBottom = '1px solid var(--border-lt)';
        const td1 = document.createElement('td'); td1.style.cssText = 'padding:10px 16px;font-family:var(--mn);font-weight:700;color:var(--acc);font-size:17px'; td1.textContent = r.konto;
        const td2 = document.createElement('td'); td2.style.cssText = 'padding:10px 16px;color:var(--tx);font-size:16px'; td2.textContent = r.naziv || '';
        const td3 = document.createElement('td'); td3.style.cssText = 'padding:10px 16px;text-align:right;font-family:var(--mn);font-weight:600;font-size:17px;color:' + sec.color; td3.textContent = fNum(Math.abs(saldo));
        const td4 = document.createElement('td'); td4.style.cssText = 'padding:10px 16px;text-align:right;font-family:var(--mn);font-size:16px;color:var(--tx3)'; td4.textContent = fNum(Math.abs(saldo) / kurs);
        tr.appendChild(td1); tr.appendChild(td2); tr.appendChild(td3); tr.appendChild(td4); tbl.appendChild(tr);
      });
      body.appendChild(tbl); secCard.appendChild(ght); secCard.appendChild(body);
    });

    // Section total as table
    const stt = document.createElement('table'); stt.style.cssText = 'width:100%;table-layout:fixed;border-collapse:collapse;background:var(--surface);border-top:2px solid var(--border)';
    stt.appendChild(mkCg());
    const sttr = document.createElement('tr');
    const st1 = document.createElement('td'); st1.colSpan = 2; st1.style.cssText = 'padding:10px 16px;font-size:17px;font-weight:700;color:var(--tx)'; st1.textContent = 'UKUPNO ' + sec.title.split(' ')[0];
    const st3 = document.createElement('td'); st3.style.cssText = 'padding:10px 16px;text-align:right;font-family:var(--mn);font-size:19px;font-weight:700;color:' + sec.color; st3.textContent = fNum(Math.abs(sectionTotal)) + ' RSD';
    const st4 = document.createElement('td'); st4.style.cssText = 'padding:10px 16px;text-align:right;font-family:var(--mn);font-size:17px;color:var(--tx3)'; st4.textContent = fNum(Math.abs(sectionTotal) / kurs) + ' USD';
    sttr.appendChild(st1); sttr.appendChild(st3); sttr.appendChild(st4); stt.appendChild(sttr);
    secCard.appendChild(stt); out.appendChild(secCard);

    if (sec.sign === 1) totalRevenue = Math.abs(sectionTotal);
    else totalExpenses = Math.abs(sectionTotal);
  });

  // Net result as table
  const netProfit = totalRevenue - totalExpenses;
  const netCard = document.createElement('div'); netCard.className = 'card'; netCard.style.cssText = 'padding:0;overflow:hidden;border:2px solid ' + (netProfit >= 0 ? 'var(--suc)' : 'var(--dng)');
  const ntt = document.createElement('table'); ntt.style.cssText = 'width:100%;table-layout:fixed;border-collapse:collapse';
  ntt.appendChild(mkCg());
  const nttr = document.createElement('tr');
  const nt1 = document.createElement('td'); nt1.colSpan = 2; nt1.style.cssText = 'padding:14px 16px';
  const netTitle = document.createElement('div'); netTitle.style.cssText = 'font-size:19px;font-weight:700;color:var(--tx)'; netTitle.textContent = netProfit >= 0 ? 'NETO DOBIT' : 'NETO GUBITAK';
  const netSub = document.createElement('div'); netSub.style.cssText = 'font-size:13px;color:var(--tx3);margin-top:4px';
  netSub.textContent = 'Prihodi: ' + fNum(totalRevenue) + ' \u2014 Rashodi: ' + fNum(totalExpenses);
  nt1.appendChild(netTitle); nt1.appendChild(netSub);
  const nt3 = document.createElement('td'); nt3.style.cssText = 'padding:14px 16px;text-align:right;font-family:var(--mn);font-size:22px;font-weight:700;color:' + (netProfit >= 0 ? 'var(--suc)' : 'var(--dng)'); nt3.textContent = fNum(Math.abs(netProfit)) + ' RSD';
  const nt4 = document.createElement('td'); nt4.style.cssText = 'padding:14px 16px;text-align:right;font-family:var(--mn);font-size:17px;color:var(--tx3)'; nt4.textContent = fNum(Math.abs(netProfit) / kurs) + ' USD';
  nttr.appendChild(nt1); nttr.appendChild(nt3); nttr.appendChild(nt4); ntt.appendChild(nttr);
  netCard.appendChild(ntt); out.appendChild(netCard);
}

function exportSheet(data, kurs, month, year, mode) {
  if (!data.length) { toast('Generišite izveštaj prvo', 'err'); return; }
  const hS = { font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 11 }, fill: { fgColor: { rgb: '0F1923' } } };
  const nS = { font: { sz: 10 }, numFmt: '#,##0.000' };
  const tS = { font: { bold: true, sz: 11 }, fill: { fgColor: { rgb: 'FEF08A' } }, numFmt: '#,##0.000' };
  const secS = { font: { bold: true, sz: 11, color: { rgb: 'D4A843' } }, fill: { fgColor: { rgb: '1A2332' } } };
  function c(v, s) { return { v, s, t: typeof v === 'number' ? 'n' : 's' }; }
  const title = mode === 'inv' ? 'Inventory' : mode === 'pl' ? 'P&L' : 'Balance Sheet';
  const rows = [];
  rows.push([c('ECOR SERBIA — ' + title + ' ' + MONTHS_SR[month - 1] + ' ' + year, { font: { bold: true, sz: 14 } }), c('', {}), c('', {}), c('', {}), c('', {}), c('', {})]);
  rows.push([c('Kurs: ' + fNum(kurs), { font: { sz: 10 } }), c('', {}), c('', {}), c('', {}), c('', {}), c('', {})]);

  if (mode === 'pl') {
    // P&L export: grouped by Revenue/Expenses with abs values, matching screen
    rows.push(['Šifra', 'Naziv', 'Iznos RSD', 'Iznos USD'].map(h => c(h, hS)));
    const revData = data.filter(r => r.konto.startsWith('6')).sort((a, b) => a.konto.localeCompare(b.konto));
    const expData = data.filter(r => r.konto.startsWith('5')).sort((a, b) => a.konto.localeCompare(b.konto));
    let totalRev = 0, totalExp = 0;
    rows.push([c('PRIHODI (Revenue)', secS), c('', secS), c('', secS), c('', secS)]);
    revData.forEach(r => {
      const s = Math.abs(parseFloat(r.saldo || 0));
      totalRev += s;
      rows.push([c(r.konto, {}), c(r.naziv || '', {}), c(s, nS), c(s / kurs, nS)]);
    });
    rows.push([c('UKUPNO PRIHODI', tS), c('', tS), c(totalRev, tS), c(totalRev / kurs, tS)]);
    rows.push([c('', {}), c('', {}), c('', {}), c('', {})]);
    rows.push([c('RASHODI (Expenses)', secS), c('', secS), c('', secS), c('', secS)]);
    expData.forEach(r => {
      const s = Math.abs(parseFloat(r.saldo || 0));
      totalExp += s;
      rows.push([c(r.konto, {}), c(r.naziv || '', {}), c(s, nS), c(s / kurs, nS)]);
    });
    rows.push([c('UKUPNO RASHODI', tS), c('', tS), c(totalExp, tS), c(totalExp / kurs, tS)]);
    rows.push([c('', {}), c('', {}), c('', {}), c('', {})]);
    const net = totalRev - totalExp;
    rows.push([c(net >= 0 ? 'NETO DOBIT' : 'NETO GUBITAK', tS), c('', tS), c(Math.abs(net), tS), c(Math.abs(net) / kurs, tS)]);
    const ws = XLSX.utils.aoa_to_sheet(rows.map(r => r.map(c => c.v)));
    ws['!cols'] = [{ wch: 12 }, { wch: 40 }, { wch: 16 }, { wch: 14 }];
    rows.forEach((row, ri) => { row.forEach((cell, ci) => { const addr = XLSX.utils.encode_cell({ r: ri, c: ci }); if (ws[addr]) ws[addr].s = cell.s; }); });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, title);
    XLSX.writeFile(wb, 'pl_' + MONTHS_SR[month - 1] + '_' + year + '.xlsx', { bookSST: false, cellStyles: true });
    toast('Excel eksportovan');
  if (state.CU) sb.rpc('log_audit', { p_user_id: state.CU.id, p_action: 'export_excel', p_entity_type: 'mesecni', p_entity_name: 'excel_export' }).then(null, e => console.error('SBIS:', e));
    return;
  }

  // BS / Inventory export: standard format
  rows.push(['Šifra', 'Naziv', 'Duguje', 'Potražuje', 'Saldo', 'Saldo USD'].map(h => c(h, hS)));
  data.sort((a, b) => a.konto.localeCompare(b.konto)).forEach(r => {
    const d = parseFloat(r.duguje || 0), cr = parseFloat(r.potrazuje || 0), s = parseFloat(r.saldo || 0);
    if (mode === 'inv' && Math.abs(s) < 0.01) return;
    rows.push([c(r.konto, {}), c(r.naziv || '', {}), c(d || null, nS), c(cr || null, nS), c(s, nS), c(s / kurs, nS)]);
  });
  const totalD = data.reduce((s, r) => s + parseFloat(r.duguje || 0), 0);
  const totalC = data.reduce((s, r) => s + parseFloat(r.potrazuje || 0), 0);
  const totalS = totalD - totalC;
  rows.push([c('UKUPNO', tS), c('', tS), c(totalD, tS), c(totalC, tS), c(totalS, tS), c(totalS / kurs, tS)]);

  const ws = XLSX.utils.aoa_to_sheet(rows.map(r => r.map(c => c.v)));
  ws['!cols'] = [{ wch: 12 }, { wch: 40 }, { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 14 }];
  rows.forEach((row, ri) => { row.forEach((cell, ci) => { const addr = XLSX.utils.encode_cell({ r: ri, c: ci }); if (ws[addr]) ws[addr].s = cell.s; }); });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, title);
  XLSX.writeFile(wb, title.toLowerCase().replace(' ', '_') + '_' + MONTHS_SR[month - 1] + '_' + year + '.xlsx', { bookSST: false, cellStyles: true });
  toast('Excel eksportovan');
  if (state.CU) sb.rpc('log_audit', { p_user_id: state.CU.id, p_action: 'export_excel', p_entity_type: 'mesecni', p_entity_name: 'excel_export' }).then(null, e => console.error('SBIS:', e));
}

export function initBsSelectors(prefix) {
  const yearSel = document.getElementById(prefix + 'Year');
  if (!yearSel || yearSel.dataset.init === 'done') return;
  yearSel.dataset.init = 'done';
  const curYear = new Date().getFullYear();
  for (let y = curYear; y >= curYear - 3; y--) {
    const o = document.createElement('option'); o.value = y; o.textContent = y; yearSel.appendChild(o);
  }
}

// ============================================================
// SHARED EXCEL STYLES
// ============================================================
const XH = { font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 11 }, fill: { fgColor: { rgb: '0F1923' } }, alignment: { horizontal: 'center' } };
const XN = { font: { sz: 10 }, numFmt: '#,##0.000' };
const XB = { font: { bold: true, sz: 11 }, numFmt: '#,##0.000', fill: { fgColor: { rgb: 'FEF08A' } } };
const XT = { font: { bold: true, sz: 12, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '0F1923' } }, numFmt: '#,##0.000' };
const XS = { font: { bold: true, sz: 11, color: { rgb: 'D4A843' } }, fill: { fgColor: { rgb: '1A2332' } } };
function xc(v, s) { return { v, s: s || {}, t: typeof v === 'number' ? 'n' : 's' }; }
function applyXs(ws, rows) { rows.forEach((r, ri) => r.forEach((cell, ci) => { const a = XLSX.utils.encode_cell({ r: ri, c: ci }); if (ws[a]) ws[a].s = cell.s; })); }

// ============================================================
// COMBINED EXPORT — 4 sheets in 1 Excel
// ============================================================
function buildCfWs(cf, kurs, monthName) {
  const o = { rsd: 0, usd: 0, ...(cf.opening || {}) };
  const inc = { customer_collection_rsd: 0, customer_collection_usd: 0, tax_refund: 0, recapitalization: 0, ...(cf.income || {}) };
  const exp = { payment_suppliers: 0, suppliers_advance: 0, fix_asset_advance: 0, salaries: 0, import_taxes: 0, bank_commission: 0, gov_taxes: 0, others: 0, ...(cf.expenses || {}) };
  const tax = { monthly_vat: 0, retained_vat: 0, ...(cf.taxes || {}) };
  const rows = [];
  rows.push([xc('ECOR SERBIA', XH), xc('', XH), xc('', XH), xc('', XH), xc('', XH), xc('', XH)]);
  rows.push([xc('CASH FLOW — ' + monthName, { font: { bold: true, sz: 12 } }), xc('', {}), xc('', {}), xc('', {}), xc('', {}), xc('', {})]);
  rows.push([xc('', {}), xc('', {}), xc('RSD', XH), xc('RSD→USD', XH), xc('USD', XH), xc('Tot. USD', XH)]);

  // *_usd i recapitalization su istorijski RSD — mergovano u DIN kolonu
  const openDin = (o.rsd || 0) + (o.usd || 0);
  const openDinUsd = openDin / kurs;
  rows.push([xc('', {}), xc('BALANCE', XB), xc(openDin, XB), xc(openDinUsd, XB), xc(null, XB), xc(openDinUsd, XB)]);
  rows.push([xc('( + )', { font: { bold: true } }), xc('INCOME', { font: { bold: true } }), xc('', {}), xc('', {}), xc('', {}), xc('', {})]);

  const ccDin = (inc.customer_collection_rsd || 0) + (inc.customer_collection_usd || 0);
  const ccDinUsd = ccDin / kurs;
  rows.push([xc('', {}), xc('Customer Collection', XN), xc(ccDin, XN), xc(ccDinUsd, XN), xc(null, XN), xc(ccDinUsd, XN)]);
  const recapDin = inc.recapitalization || 0;
  const recapDinUsd = recapDin / kurs;
  rows.push([xc('', {}), xc('Recapitalization', XN), xc(recapDin, XN), xc(recapDinUsd, XN), xc(null, XN), xc(recapDinUsd, XN)]);
  rows.push([xc('', {}), xc('Tax Refund & Other', XN), xc(inc.tax_refund, XN), xc(inc.tax_refund / kurs, XN), xc(null, XN), xc(inc.tax_refund / kurs, XN)]);

  const tID = ccDin + recapDin + inc.tax_refund;
  rows.push([xc('', {}), xc('Total Income', XB), xc(tID, XB), xc(tID / kurs, XB), xc(null, XB), xc(tID / kurs, XB)]);

  rows.push([xc('( - )', { font: { bold: true } }), xc('EXPENSES', { font: { bold: true } }), xc('', {}), xc('', {}), xc('', {}), xc('', {})]);
  rows.push([xc('', {}), xc('Payment to Suppliers', XN), xc(-exp.payment_suppliers, XN), xc(-exp.payment_suppliers / kurs, XN), xc(null, XN), xc(-exp.payment_suppliers / kurs, XN)]);
  if ((exp.suppliers_advance||0) > 0) rows.push([xc('', {}), xc('Suppliers Advance', XN), xc(-(exp.suppliers_advance||0), XN), xc(-(exp.suppliers_advance||0) / kurs, XN), xc(null, XN), xc(-(exp.suppliers_advance||0) / kurs, XN)]);
  if ((exp.fix_asset_advance||0) > 0) rows.push([xc('', {}), xc('Fix Asset Advance', XN), xc(-(exp.fix_asset_advance||0), XN), xc(-(exp.fix_asset_advance||0) / kurs, XN), xc(null, XN), xc(-(exp.fix_asset_advance||0) / kurs, XN)]);
  rows.push([xc('', {}), xc('Salaries', XN), xc(-exp.salaries, XN), xc(-exp.salaries / kurs, XN), xc(null, XN), xc(-exp.salaries / kurs, XN)]);
  if (exp.import_taxes > 0) rows.push([xc('', {}), xc('Import/Export Taxes', XN), xc(-exp.import_taxes, XN), xc(-exp.import_taxes / kurs, XN), xc(null, XN), xc(-exp.import_taxes / kurs, XN)]);
  if (exp.bank_commission > 0) rows.push([xc('', {}), xc('Bank Commission', XN), xc(-exp.bank_commission, XN), xc(-exp.bank_commission / kurs, XN), xc(null, XN), xc(-exp.bank_commission / kurs, XN)]);
  rows.push([xc('', {}), xc('Gov. Taxes & Contributions', XN), xc(-exp.gov_taxes, XN), xc(-exp.gov_taxes / kurs, XN), xc(null, XN), xc(-exp.gov_taxes / kurs, XN)]);
  if (exp.others > 0) rows.push([xc('', {}), xc('Others', XN), xc(-exp.others, XN), xc(-exp.others / kurs, XN), xc(null, XN), xc(-exp.others / kurs, XN)]);

  const tED = -(exp.payment_suppliers + (exp.suppliers_advance||0) + (exp.fix_asset_advance||0) + exp.salaries + exp.import_taxes + exp.bank_commission + exp.gov_taxes + exp.others);
  rows.push([xc('', {}), xc('Total Expenses', XB), xc(tED, XB), xc(tED / kurs, XB), xc(null, XB), xc(tED / kurs, XB)]);

  const fT = (tID + tED) / kurs;
  rows.push([xc('', {}), xc('FINAL BALANCE', XT), xc(tID + tED, XT), xc(fT, XT), xc(null, XT), xc(fT, XT)]);
  rows.push([xc('', {}), xc('', {}), xc('', {}), xc('', {}), xc('', {}), xc('', {})]);

  if (tax.monthly_vat > 0) rows.push([xc('', {}), xc('Monthly VAT', XN), xc(tax.monthly_vat, XN), xc(tax.monthly_vat / kurs, XN), xc(null, XN), xc(tax.monthly_vat / kurs, XN)]);
  rows.push([xc('', {}), xc('Retained VAT & Income Tax', XN), xc(tax.retained_vat, XN), xc(tax.retained_vat / kurs, XN), xc(null, XN), xc(tax.retained_vat / kurs, XN)]);

  const nC = fT - (tax.monthly_vat / kurs) - (tax.retained_vat / kurs);
  rows.push([xc('( = )', { font: { bold: true } }), xc('NET CASH', XT), xc(null, XT), xc(null, XT), xc(null, XT), xc(nC, XT)]);
  rows.push([xc('', {}), xc('', {}), xc('', {}), xc('', {}), xc('', {}), xc('', {})]);
  rows.push([xc('ER', { font: { bold: true } }), xc(kurs, { font: { bold: true }, numFmt: '#,##0.0000' }), xc('', {}), xc('', {}), xc('', {}), xc('', {})]);

  const ws = XLSX.utils.aoa_to_sheet(rows.map(r => r.map(c => c.v)));
  ws['!cols'] = [{ wch: 8 }, { wch: 32 }, { wch: 16 }, { wch: 14 }, { wch: 14 }, { wch: 14 }];
  applyXs(ws, rows);
  return ws;
}

function buildDataWs(data, kurs, monthName, mode) {
  const title = mode === 'inv' ? 'Inventory' : mode === 'pl' ? 'P&L' : 'Balance Sheet';
  const rows = [];
  rows.push([xc('ECOR SERBIA — ' + title + ' ' + monthName, { font: { bold: true, sz: 14 } }), xc('', {}), xc('', {}), xc('', {}), xc('', {}), xc('', {})]);
  rows.push([xc('Kurs: ' + fNum(kurs), { font: { sz: 10 } }), xc('', {}), xc('', {}), xc('', {}), xc('', {}), xc('', {})]);
  rows.push(['Šifra', 'Naziv', 'Duguje', 'Potražuje', 'Saldo', 'Saldo USD'].map(h => xc(h, XH)));
  data.sort((a, b) => a.konto.localeCompare(b.konto)).forEach(r => {
    const d = parseFloat(r.duguje || 0), cr = parseFloat(r.potrazuje || 0), s = parseFloat(r.saldo || 0);
    if (mode === 'inv' && Math.abs(s) < 0.01) return;
    rows.push([xc(r.konto, {}), xc(r.naziv || '', {}), xc(d || null, XN), xc(cr || null, XN), xc(s, XN), xc(s / kurs, XN)]);
  });
  const totalD = data.reduce((s, r) => s + parseFloat(r.duguje || 0), 0);
  const totalC = data.reduce((s, r) => s + parseFloat(r.potrazuje || 0), 0);
  const totalS = totalD - totalC;
  rows.push([xc('UKUPNO', XB), xc('', XB), xc(totalD, XB), xc(totalC, XB), xc(totalS, XB), xc(totalS / kurs, XB)]);

  const ws = XLSX.utils.aoa_to_sheet(rows.map(r => r.map(c => c.v)));
  ws['!cols'] = [{ wch: 12 }, { wch: 40 }, { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 14 }];
  applyXs(ws, rows);
  return ws;
}

export async function exportMesecniXlsx() {
  if (!state.CU) { toast('Niste prijavljeni', 'err'); return; }
  const month = parseInt(document.getElementById('cfMonth').value);
  const year = parseInt(document.getElementById('cfYear').value);
  const kurs = parseFloat(document.getElementById('cfKurs').value) || 108;
  const monthName = MONTHS_SR[month - 1] + ' ' + year;

  showLoading();
  try {
    const [cfRes, bsRes] = await Promise.all([
      sb.rpc('calculate_cashflow', { p_user_id: state.CU.id, p_year: year, p_month: month, p_kurs: kurs }),
      sb.rpc('calculate_balance_sheet', { p_user_id: state.CU.id, p_year: year, p_month: month, p_kurs: kurs })
    ]);
    if (cfRes.error) throw cfRes.error;
    if (bsRes.error) throw bsRes.error;

    const bsData = bsRes.data || [];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, buildCfWs(cfRes.data, kurs, monthName), 'Cash Flow');
    XLSX.utils.book_append_sheet(wb, buildDataWs(bsData, kurs, monthName, 'bs'), 'Balance Sheet');
    XLSX.utils.book_append_sheet(wb, buildDataWs(bsData.filter(r => r.konto.startsWith('1')), kurs, monthName, 'inv'), 'Inventory');
    XLSX.utils.book_append_sheet(wb, buildDataWs(bsData.filter(r => r.konto.startsWith('5') || r.konto.startsWith('6')), kurs, monthName, 'pl'), 'P&L');

    XLSX.writeFile(wb, 'Monthly_Report_' + monthName.replace(' ', '_') + '.xlsx', { bookSST: false, cellStyles: true });
    toast('Mesečni izveštaj eksportovan (4 sheeta)');
  } catch (e) { toast('Greška: ' + (e.message || e), 'err'); }
  hideLoading();
}

// ============================================================
// YEARLY CASHFLOW EXPORT — 12 sheets in 1 Excel
// ============================================================
export async function exportCashflowYearXlsx() {
  if (!state.CU) { toast('Niste prijavljeni', 'err'); return; }
  const year = parseInt(document.getElementById('cfYear').value);
  const kurs = parseFloat(document.getElementById('cfKurs').value) || 108;

  showLoading();
  try {
    const promises = [];
    for (let m = 1; m <= 12; m++) {
      promises.push(sb.rpc('calculate_cashflow', { p_user_id: state.CU.id, p_year: year, p_month: m, p_kurs: kurs }));
    }
    const results = await Promise.all(promises);
    const wb = XLSX.utils.book_new();
    const sheetNames = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
    let hasData = false;
    for (let m = 0; m < 12; m++) {
      if (results[m].error) continue;
      const cf = results[m].data;
      if (!cf) continue;
      const monthName = MONTHS_SR[m] + ' ' + year;
      XLSX.utils.book_append_sheet(wb, buildCfWs(cf, kurs, monthName), sheetNames[m] + ' Cash Flow');
      hasData = true;
    }
    if (!hasData) { toast('Nema podataka za ' + year, 'err'); hideLoading(); return; }
    XLSX.writeFile(wb, 'Cashflow_' + year + '.xlsx', { bookSST: false, cellStyles: true });
    toast('Godišnji cashflow eksportovan (' + wb.SheetNames.length + ' meseci)');
  } catch (e) { toast('Greška: ' + (e.message || e), 'err'); }
  hideLoading();
}

// ============================================================
// BS 12 MONTHS
// ============================================================
let lastBs12 = null;
const MSH = ['Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Jun', 'Jul', 'Avg', 'Sep', 'Okt', 'Nov', 'Dec'];

function calcCat(mData, testFn) {
  return (mData || []).filter(r => testFn(r.konto)).reduce((s, r) => s + parseFloat(r.saldo || 0), 0);
}

// Detailed BS 12M categories matching original Excel format
const BS12_CATS = [
  // Current Assets
  { key: 'cash', label: 'Cash & Banks', test: k => k.startsWith('241') || k.startsWith('244') },
  { key: 'recv', label: 'Account Receivables', test: k => k.startsWith('204') || k.startsWith('205') },
  { key: 'recvIC', label: 'Account Receivables Interco.', test: k => k.startsWith('200') || k.startsWith('201') || k.startsWith('202') || k.startsWith('203') },
  { key: 'inv', label: 'Inventory', test: k => k.startsWith('1') && !k.startsWith('15') },
  { key: 'oca', label: 'Other Current Assets', test: k => (k.startsWith('2') && !k.startsWith('241') && !k.startsWith('244') && !k.startsWith('204') && !k.startsWith('205') && !k.startsWith('200') && !k.startsWith('201') && !k.startsWith('202') && !k.startsWith('203')) || k.startsWith('15') || k.startsWith('00') },
  // Fixed Assets
  { key: 'build', label: 'Buildings and Construction in progress', test: k => k.startsWith('021') || k.startsWith('024') },
  { key: 'mach', label: 'Plant Machinery & Equipment', test: k => k.startsWith('022') || k.startsWith('023') },
  { key: 'land', label: 'Land, Plot, Buildings', test: k => k.startsWith('020') },
  { key: 'ppeAdv', label: 'Advances for PP&E', test: k => k.startsWith('09') },
  { key: 'bio', label: 'Biological Agents', test: k => k.startsWith('03') },
  { key: 'depr', label: 'Accum. Depreciation', test: k => k.startsWith('014') || k.startsWith('029') },
  { key: 'oFixed', label: 'Other Fixed Assets', test: k => (k.startsWith('025') || k.startsWith('026') || k.startsWith('027') || k.startsWith('028') || k.startsWith('04') || k.startsWith('05') || k.startsWith('06') || k.startsWith('07') || k.startsWith('08')) && !k.startsWith('09') },
  // Intangible
  { key: 'intang', label: 'Intangible Assets', test: k => k.startsWith('01') && !k.startsWith('014') },
  // Liabilities - Current
  { key: 'payTrade', label: 'Account Payables', test: k => k.startsWith('435') || k.startsWith('436') },
  { key: 'accrued', label: 'Accrued Expenses', test: k => k.startsWith('4') && !k.startsWith('435') && !k.startsWith('436') && !k.startsWith('40') && !k.startsWith('41') && !k.startsWith('42') },
  // Liabilities - Long term
  { key: 'ltl', label: 'Long Term Liabilities', test: k => k.startsWith('40') || k.startsWith('41') || k.startsWith('42') },
  // Equity
  { key: 'capital', label: 'Capital', test: k => k.startsWith('30') || k.startsWith('31') || k.startsWith('32') || k.startsWith('33') },
  { key: 'retEarn', label: 'Previous Period Gain/Loss', test: k => k.startsWith('34') || k.startsWith('35') || k.startsWith('36') || k.startsWith('37') || k.startsWith('38') || k.startsWith('39') },
  // P&L accounts for current period
  { key: '_rev', label: '_rev', test: k => k.startsWith('6') },
  { key: '_exp', label: '_exp', test: k => k.startsWith('5') },
  // Class 7 — opening/closing (usually nets to 0)
  { key: '_cl7', label: '_cl7', test: k => k.startsWith('7') },
];

function computeBS12(monthsData) {
  const v = {};
  BS12_CATS.forEach(it => { v[it.key] = []; for (let m = 0; m < 12; m++) v[it.key].push(calcCat(monthsData[m], it.test)); });
  v.curAssets = []; v.fixAssets = []; v.totalA = [];
  v.curLiab = []; v.totalL = []; v.curPeriodGL = []; v.totalEq = []; v.totalLE = []; v.check = [];
  for (let m = 0; m < 12; m++) {
    v.curAssets.push(v.cash[m] + v.recv[m] + v.recvIC[m] + v.inv[m] + v.oca[m]);
    v.fixAssets.push(v.build[m] + v.mach[m] + v.land[m] + v.ppeAdv[m] + v.bio[m] + v.depr[m] + v.oFixed[m]);
    v.totalA.push(v.curAssets[m] + v.fixAssets[m] + v.intang[m]);
    v.curLiab.push(-(v.payTrade[m] + v.accrued[m]));
    v.totalL.push(v.curLiab[m] + (-v.ltl[m]));
    v.curPeriodGL.push(-v._rev[m] - v._exp[m]);
    v.totalEq.push(-v.capital[m] - v.retEarn[m] + v.curPeriodGL[m]);
    v.totalLE.push(v.totalL[m] + v.totalEq[m]);
    v.check.push(v.totalA[m] - v.totalLE[m] + (v._cl7[m] || 0));
  }
  return v;
}

export async function generateBS12() {
  if (!state.CU) { toast('Niste prijavljeni', 'err'); return; }
  const year = parseInt(document.getElementById('bs12Year').value);
  const kurs = parseFloat(document.getElementById('bs12Kurs').value) || 108;

  showLoading();
  try {
    const promises = [];
    for (let m = 1; m <= 12; m++) {
      promises.push(sb.rpc('calculate_balance_sheet', { p_user_id: state.CU.id, p_year: year, p_month: m, p_kurs: kurs }));
    }
    const results = await Promise.all(promises);
    results.forEach((r, i) => { if (r.error) console.warn('Month ' + (i+1) + ' error:', r.error); });
    const monthsData = results.map(r => r.data || []);
    lastBs12 = { year, kurs, monthsData };
    renderBS12(monthsData, kurs, year);
    document.getElementById('bs12ExpBtn').style.display = '';
    toast('BS 12 Months generisan za ' + year);
  } catch (e) { toast('Greška: ' + (e.message || e), 'err'); }
  hideLoading();
}

function renderBS12(monthsData, kurs, year) {
  const out = document.getElementById('bs12Output'); out.textContent = '';
  const v = computeBS12(monthsData);

  const rows = [
    { type: 'sec', label: 'ASSETS', color: 'var(--acc)' },
    { type: 'row', label: 'Cash & Banks', vals: v.cash, sign: 1 },
    { type: 'row', label: 'Account Receivables', vals: v.recv, sign: 1 },
    { type: 'row', label: 'Account Receivables Interco.', vals: v.recvIC, sign: 1 },
    { type: 'row', label: 'Inventory', vals: v.inv, sign: 1 },
    { type: 'row', label: 'Other Current Assets', vals: v.oca, sign: 1 },
    { type: 'sub', label: 'CURRENT ASSETS', vals: v.curAssets, color: 'var(--acc)' },
    { type: 'gap' },
    { type: 'row', label: 'Buildings and Construction in progress', vals: v.build, sign: 1 },
    { type: 'row', label: 'Plant Machinery & Equipment', vals: v.mach, sign: 1 },
    { type: 'row', label: 'Land, Plot, Buildings', vals: v.land, sign: 1 },
    { type: 'row', label: 'Advances for PP&E', vals: v.ppeAdv, sign: 1 },
    { type: 'row', label: 'Biological Agents', vals: v.bio, sign: 1 },
    { type: 'row', label: 'Accum. Depreciation', vals: v.depr, sign: 1, neg: true },
    { type: 'row', label: 'Other Fixed Assets', vals: v.oFixed, sign: 1 },
    { type: 'sub', label: 'FIXED ASSETS', vals: v.fixAssets, color: 'var(--acc)' },
    { type: 'gap' },
    { type: 'row', label: 'Intangible Assets', vals: v.intang, sign: 1 },
    { type: 'tot', label: 'TOTAL ASSETS', vals: v.totalA, color: 'var(--acc)' },
    { type: 'gap' },
    { type: 'sec', label: 'LIABILITIES', color: 'var(--dng)' },
    { type: 'row', label: 'Account Payables', vals: v.payTrade, sign: -1 },
    { type: 'row', label: 'Accrued Expenses', vals: v.accrued, sign: -1 },
    { type: 'sub', label: 'CURRENT LIABILITIES', vals: v.curLiab, color: 'var(--dng)' },
    { type: 'row', label: 'Long Term Liabilities', vals: v.ltl, sign: -1 },
    { type: 'tot', label: 'TOTAL LIABILITIES', vals: v.totalL, color: 'var(--dng)' },
    { type: 'gap' },
    { type: 'sec', label: 'EQUITY', color: 'var(--suc)' },
    { type: 'row', label: 'Capital', vals: v.capital, sign: -1 },
    { type: 'row', label: 'Previous Period Gain/Loss', vals: v.retEarn, sign: -1 },
    { type: 'row', label: 'Gain/Loss Current Period', vals: v.curPeriodGL, sign: 1 },
    { type: 'tot', label: 'TOTAL EQUITY', vals: v.totalEq, color: 'var(--suc)' },
    { type: 'gap' },
    { type: 'tot', label: 'TOTAL LIABILITIES + EQUITY', vals: v.totalLE, color: 'var(--tx)' },
    { type: 'chk', label: 'CHECK (A \u2212 L \u2212 E)', vals: v.check },
  ];

  // Card
  const card = document.createElement('div'); card.className = 'card'; card.style.cssText = 'padding:0;overflow-x:auto';
  const hdr = document.createElement('div'); hdr.style.cssText = 'padding:16px 20px;border-bottom:2px solid var(--acc)';
  const h2 = document.createElement('h2'); h2.style.cssText = 'font-size:24px;color:var(--acc)'; h2.textContent = 'BALANCE SHEET 12 MONTHS \u2014 ' + year;
  const sub = document.createElement('div'); sub.style.cssText = 'font-size:13px;color:var(--tx3);margin-top:2px'; sub.textContent = 'ECOR SERBIA \u00B7 Kurs: ' + fNum(kurs);
  hdr.appendChild(h2); hdr.appendChild(sub); card.appendChild(hdr);

  // border-separate is required for sticky to work on td elements
  const tbl = document.createElement('table'); tbl.style.cssText = 'border-collapse:separate;border-spacing:0;table-layout:auto;width:max-content;min-width:100%';
  const stickyBase = 'position:sticky;left:0;z-index:1;min-width:220px;white-space:nowrap;box-shadow:4px 0 8px -2px rgba(0,0,0,0.4)';

  // Thead
  const thead = document.createElement('thead');
  const thr = document.createElement('tr');
  const th0 = document.createElement('th'); th0.style.cssText = 'padding:12px 16px;text-align:left;font-size:13px;color:var(--tx3);background:var(--raised);z-index:2;border-bottom:2px solid var(--border);border-right:2px solid var(--border);' + stickyBase; th0.textContent = '';
  thr.appendChild(th0);
  MSH.forEach((m, i) => { const th = document.createElement('th'); th.textContent = m; th.style.cssText = 'padding:12px 14px;font-size:13px;text-align:right;color:var(--tx3);text-transform:uppercase;letter-spacing:.5px;white-space:nowrap;background:var(--raised);border-bottom:2px solid var(--border)' + (i < 11 ? ';border-right:1px solid var(--border-lt)' : ''); thr.appendChild(th); });
  thead.appendChild(thr); tbl.appendChild(thead);

  const tbody = document.createElement('tbody');
  rows.forEach(r => {
    if (r.type === 'gap') {
      const tr = document.createElement('tr');
      const tdS = document.createElement('td'); tdS.style.cssText = 'height:8px;background:var(--raised);border-right:2px solid var(--border);' + stickyBase;
      const tdR = document.createElement('td'); tdR.colSpan = 12; tdR.style.height = '8px';
      tr.appendChild(tdS); tr.appendChild(tdR); tbody.appendChild(tr); return;
    }
    if (r.type === 'sec') {
      const tr = document.createElement('tr');
      const tdS = document.createElement('td'); tdS.style.cssText = 'padding:12px 16px;font-weight:700;font-size:16px;color:' + r.color + ';background:var(--surface);border-right:2px solid var(--border);' + stickyBase;
      tdS.textContent = r.label;
      const tdR = document.createElement('td'); tdR.colSpan = 12; tdR.style.cssText = 'background:var(--surface)';
      tr.appendChild(tdS); tr.appendChild(tdR); tbody.appendChild(tr); return;
    }

    const tr = document.createElement('tr');
    const isSub = r.type === 'sub';
    const isTot = r.type === 'tot';
    const bg = (isTot || isSub) ? 'var(--surface)' : 'var(--raised)';
    const tdL = document.createElement('td'); tdL.style.cssText = 'padding:10px 16px;font-size:14px;background:' + bg + ';border-right:2px solid var(--border);border-bottom:1px solid var(--border-lt);' + stickyBase;
    if (r.type === 'row') { tdL.style.paddingLeft = '28px'; tdL.style.color = r.neg ? 'var(--dng)' : 'var(--tx)'; }
    if (isTot) { tdL.style.fontWeight = '700'; tdL.style.color = r.color; tdL.style.fontSize = '15px'; }
    if (isSub) { tdL.style.fontWeight = '600'; tdL.style.color = r.color; tdL.style.fontStyle = 'italic'; }
    if (r.type === 'chk') { tdL.style.fontWeight = '700'; tdL.style.fontStyle = 'italic'; tdL.style.color = 'var(--tx3)'; }
    tdL.textContent = r.label; tr.appendChild(tdL);

    for (let m = 0; m < 12; m++) {
      const td = document.createElement('td'); td.style.cssText = 'padding:10px 14px;text-align:right;font-family:var(--mn);font-size:14px;white-space:nowrap;border-bottom:1px solid var(--border-lt)' + (m < 11 ? ';border-right:1px solid var(--border-lt)' : '');
      let val = r.type === 'row' ? r.vals[m] * r.sign : r.vals[m];
      if (isTot) { td.style.fontWeight = '700'; td.style.background = 'var(--surface)'; td.style.borderTop = '2px solid var(--border)'; td.style.color = r.color; }
      if (isSub) { td.style.fontWeight = '600'; td.style.background = 'var(--surface)'; td.style.borderTop = '1px solid var(--border)'; td.style.color = r.color; td.style.fontStyle = 'italic'; }
      if (r.type === 'chk') { td.style.fontStyle = 'italic'; td.style.color = Math.abs(val) < 1 ? 'var(--suc)' : 'var(--dng)'; }
      td.textContent = fNum(val);
      tr.appendChild(td);
    }
    tbody.appendChild(tr);
  });
  tbl.appendChild(tbody); card.appendChild(tbl); out.appendChild(card);
}

// Subtotal Excel style
const XSB = { font: { bold: true, sz: 10, italic: true }, numFmt: '#,##0.000', fill: { fgColor: { rgb: 'E8E2D0' } } };

export function exportBs12Xlsx() {
  if (!lastBs12) { toast('Generišite BS 12 Months prvo', 'err'); return; }
  const { year, kurs, monthsData } = lastBs12;
  const v = computeBS12(monthsData);

  const xlRows = [
    { label: 'ASSETS', type: 'sec' },
    { label: '  Cash & Banks', vals: v.cash, sign: 1 },
    { label: '  Account Receivables', vals: v.recv, sign: 1 },
    { label: '  Account Receivables Interco.', vals: v.recvIC, sign: 1 },
    { label: '  Inventory', vals: v.inv, sign: 1 },
    { label: '  Other Current Assets', vals: v.oca, sign: 1 },
    { label: '  CURRENT ASSETS', vals: v.curAssets, type: 'sub' },
    { label: '', type: 'gap' },
    { label: '  Buildings and Construction', vals: v.build, sign: 1 },
    { label: '  Plant Machinery & Equipment', vals: v.mach, sign: 1 },
    { label: '  Land, Plot, Buildings', vals: v.land, sign: 1 },
    { label: '  Advances for PP&E', vals: v.ppeAdv, sign: 1 },
    { label: '  Biological Agents', vals: v.bio, sign: 1 },
    { label: '  Accum. Depreciation', vals: v.depr, sign: 1 },
    { label: '  Other Fixed Assets', vals: v.oFixed, sign: 1 },
    { label: '  FIXED ASSETS', vals: v.fixAssets, type: 'sub' },
    { label: '', type: 'gap' },
    { label: '  Intangible Assets', vals: v.intang, sign: 1 },
    { label: 'TOTAL ASSETS', vals: v.totalA, type: 'tot' },
    { label: '', type: 'gap' },
    { label: 'LIABILITIES', type: 'sec' },
    { label: '  Account Payables', vals: v.payTrade, sign: -1 },
    { label: '  Accrued Expenses', vals: v.accrued, sign: -1 },
    { label: '  CURRENT LIABILITIES', vals: v.curLiab, type: 'sub' },
    { label: '  Long Term Liabilities', vals: v.ltl, sign: -1 },
    { label: 'TOTAL LIABILITIES', vals: v.totalL, type: 'tot' },
    { label: '', type: 'gap' },
    { label: 'EQUITY', type: 'sec' },
    { label: '  Capital', vals: v.capital, sign: -1 },
    { label: '  Previous Period Gain/Loss', vals: v.retEarn, sign: -1 },
    { label: '  Gain/Loss Current Period', vals: v.curPeriodGL, sign: 1 },
    { label: 'TOTAL EQUITY', vals: v.totalEq, type: 'tot' },
    { label: '', type: 'gap' },
    { label: 'TOTAL LIABILITIES + EQUITY', vals: v.totalLE, type: 'tot' },
    { label: 'CHECK (A-L-E)', vals: v.check, type: 'chk' },
  ];

  const rows = [];
  rows.push([xc('ECOR SERBIA — BS 12 Months ' + year, { font: { bold: true, sz: 14 } }), ...MSH.map(() => xc('', {}))]);
  rows.push([xc('Kurs: ' + fNum(kurs), { font: { sz: 10 } }), ...MSH.map(() => xc('', {}))]);
  rows.push([xc('', XH), ...MSH.map(m => xc(m, XH))]);

  xlRows.forEach(r => {
    if (r.type === 'gap') { rows.push([xc('', {}), ...MSH.map(() => xc('', {}))]); return; }
    if (r.type === 'sec') { rows.push([xc(r.label, XS), ...MSH.map(() => xc('', XS))]); return; }
    const st = r.type === 'tot' ? XB : r.type === 'sub' ? XSB : r.type === 'chk' ? XT : XN;
    const labelSt = r.type === 'tot' ? XB : r.type === 'sub' ? XSB : r.type === 'chk' ? XT : {};
    const mVals = r.vals ? r.vals.map(val => xc(r.sign ? val * r.sign : val, st)) : MSH.map(() => xc('', st));
    rows.push([xc(r.label, labelSt), ...mVals]);
  });

  const ws = XLSX.utils.aoa_to_sheet(rows.map(r => r.map(c => c.v)));
  ws['!cols'] = [{ wch: 34 }, ...MSH.map(() => ({ wch: 14 }))];
  applyXs(ws, rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'BS 12 Months');
  XLSX.writeFile(wb, 'BS_12Months_' + year + '.xlsx', { bookSST: false, cellStyles: true });
  toast('Excel eksportovan');
  if (state.CU) sb.rpc('log_audit', { p_user_id: state.CU.id, p_action: 'export_excel', p_entity_type: 'mesecni', p_entity_name: 'excel_export' }).then(null, e => console.error('SBIS:', e));
}

// ============================================================
// P&L 12 MONTHS
// ============================================================
let lastPl12 = null;

const PL12_ITEMS = [
  // Revenue
  { key: 'sales', label: 'Sales Revenue (60-61)', test: k => k.startsWith('60') || k.startsWith('61') },
  { key: 'orev', label: 'Other Income (62-64, 66-68)', test: k => /^6[2-8]/.test(k) && !k.startsWith('65') },
  // COGS breakdown
  { key: 'rawMat', label: 'Raw Materials Costs', test: k => k.startsWith('51') && !k.startsWith('512') },
  { key: 'mfg', label: 'Manufacturing Costs', test: k => k.startsWith('50') },
  { key: 'labor', label: 'Direct Labor Costs', test: k => k.startsWith('52') },
  { key: 'energy', label: 'Energy Costs', test: k => k.startsWith('512') },
  { key: 'maint', label: 'Maintenance Costs', test: k => k.startsWith('541') },
  { key: 'wareh', label: 'Warehousing Costs', test: k => k.startsWith('540') },
  // OpEx
  { key: 'dep', label: 'Depreciation (53)', test: k => k.startsWith('53') },
  { key: 'sga', label: 'Selling, General & Admin', test: k => k.startsWith('54') && !k.startsWith('540') && !k.startsWith('541') },
  // Below EBIT
  { key: 'finExp', label: 'Interest Expenses', test: k => k.startsWith('55') },
  { key: 'finInc', label: 'Interest Income', test: k => k.startsWith('65') },
  { key: 'otherNonOp', label: 'Other non-operating', test: k => k.startsWith('56') || k.startsWith('57') || k.startsWith('58') },
];

export async function generatePl12() {
  if (!state.CU) { toast('Niste prijavljeni', 'err'); return; }
  const year = parseInt(document.getElementById('pl12Year').value);
  const kurs = parseFloat(document.getElementById('pl12Kurs').value) || 108;

  showLoading();
  try {
    const promises = [];
    for (let m = 1; m <= 12; m++) {
      promises.push(sb.rpc('calculate_balance_sheet', { p_user_id: state.CU.id, p_year: year, p_month: m, p_kurs: kurs }));
    }
    const results = await Promise.all(promises);
    results.forEach((r, i) => { if (r.error) console.warn('Month ' + (i+1) + ' error:', r.error); });
    const monthsData = results.map(r => r.data || []);
    lastPl12 = { year, kurs, monthsData };
    renderPl12(monthsData, kurs, year);
    document.getElementById('pl12ExpBtn').style.display = '';
    toast('P&L 12 Months generisan za ' + year);
  } catch (e) { toast('Greška: ' + (e.message || e), 'err'); }
  hideLoading();
}

function computePl12(monthsData) {
  const v = {};
  PL12_ITEMS.forEach(it => { v[it.key] = []; for (let m = 0; m < 12; m++) v[it.key].push(calcCat(monthsData[m], it.test)); });
  v.totalRev = []; v.revGrowth = []; v.totalCogs = []; v.gross = []; v.grossPct = [];
  v.totalOpex = []; v.ebit = []; v.ebitPct = [];
  v.interestNet = []; v.net = []; v.netPct = [];
  for (let m = 0; m < 12; m++) {
    // Revenue (class 6 saldo is negative, negate to positive)
    v.totalRev.push(-(v.sales[m] + v.orev[m]));
    v.revGrowth.push(m > 0 && v.totalRev[m - 1] !== 0 ? ((v.totalRev[m] - v.totalRev[m - 1]) / Math.abs(v.totalRev[m - 1])) * 100 : null);
    // COGS (class 5 saldo is positive = debit)
    v.totalCogs.push(v.rawMat[m] + v.mfg[m] + v.labor[m] + v.energy[m] + v.maint[m] + v.wareh[m]);
    // Gross
    v.gross.push(v.totalRev[m] - v.totalCogs[m]);
    v.grossPct.push(v.totalRev[m] !== 0 ? (v.gross[m] / v.totalRev[m]) * 100 : null);
    // OpEx
    v.totalOpex.push(v.dep[m] + v.sga[m]);
    // EBIT
    v.ebit.push(v.gross[m] - v.totalOpex[m]);
    v.ebitPct.push(v.totalRev[m] !== 0 ? (v.ebit[m] / v.totalRev[m]) * 100 : null);
    // Interest net (income is negative saldo from class 6, negate; expense is positive from class 5)
    v.interestNet.push(-v.finInc[m] - v.finExp[m]);
    // Net Income
    v.net.push(v.ebit[m] + v.interestNet[m] - v.otherNonOp[m]);
    v.netPct.push(v.totalRev[m] !== 0 ? (v.net[m] / v.totalRev[m]) * 100 : null);
  }
  return v;
}

function renderPl12(monthsData, kurs, year) {
  const out = document.getElementById('pl12Output'); out.textContent = '';
  const v = computePl12(monthsData);

  const tableRows = [
    { type: 'sec', label: 'REVENUE', color: 'var(--suc)' },
    { type: 'row', label: 'Sales Revenue (60-61)', key: 'sales', sign: -1 },
    { type: 'row', label: 'Other Income (62-64, 66-68)', key: 'orev', sign: -1 },
    { type: 'tot', label: 'Total Revenue', key: 'totalRev', color: 'var(--suc)' },
    { type: 'pct', label: 'M/M Revenue Growth (%)', key: 'revGrowth' },
    { type: 'gap' },
    { type: 'sec', label: 'COST OF GOODS SOLD', color: 'var(--dng)' },
    { type: 'row', label: 'Raw Materials Costs', key: 'rawMat', sign: 1 },
    { type: 'row', label: 'Manufacturing Costs', key: 'mfg', sign: 1 },
    { type: 'row', label: 'Direct Labor Costs', key: 'labor', sign: 1 },
    { type: 'row', label: 'Energy Costs', key: 'energy', sign: 1 },
    { type: 'row', label: 'Maintenance Costs', key: 'maint', sign: 1 },
    { type: 'row', label: 'Warehousing Costs', key: 'wareh', sign: 1 },
    { type: 'tot', label: 'Cost of Goods Sold', key: 'totalCogs', color: 'var(--dng)' },
    { type: 'gap' },
    { type: 'der', label: 'GROSS MARGIN', key: 'gross', color: 'var(--acc)' },
    { type: 'pct', label: 'Gross Margin (%)', key: 'grossPct' },
    { type: 'gap' },
    { type: 'sec', label: 'OPERATING EXPENSES', color: 'var(--dng)' },
    { type: 'row', label: 'Depreciation', key: 'dep', sign: 1 },
    { type: 'row', label: 'Selling, General & Admin', key: 'sga', sign: 1 },
    { type: 'tot', label: 'Total OpEx', key: 'totalOpex', color: 'var(--dng)' },
    { type: 'gap' },
    { type: 'der', label: 'OPERATING INCOME (EBIT)', key: 'ebit', color: null },
    { type: 'pct', label: 'Operating Income Margin (%)', key: 'ebitPct' },
    { type: 'gap' },
    { type: 'row', label: 'Interest Income / (Expenses)', key: 'interestNet', sign: 1 },
    { type: 'row', label: 'Other Non-operating', key: 'otherNonOp', sign: -1 },
    { type: 'gap' },
    { type: 'der', label: 'NET INCOME', key: 'net', color: null },
    { type: 'pct', label: 'Net Income Margin (%)', key: 'netPct' },
  ];

  const card = document.createElement('div'); card.className = 'card'; card.style.cssText = 'padding:0;overflow-x:auto';
  const hdr = document.createElement('div'); hdr.style.cssText = 'padding:16px 20px;border-bottom:2px solid var(--suc)';
  const h2 = document.createElement('h2'); h2.style.cssText = 'font-size:24px;color:var(--suc)'; h2.textContent = 'P&L 12 MONTHS \u2014 ' + year;
  const sub2 = document.createElement('div'); sub2.style.cssText = 'font-size:13px;color:var(--tx3);margin-top:2px'; sub2.textContent = 'ECOR SERBIA \u00B7 Kurs: ' + fNum(kurs);
  hdr.appendChild(h2); hdr.appendChild(sub2); card.appendChild(hdr);

  const tbl = document.createElement('table'); tbl.style.cssText = 'border-collapse:separate;border-spacing:0;table-layout:auto;width:max-content;min-width:100%';
  const stickyBase = 'position:sticky;left:0;z-index:1;min-width:220px;white-space:nowrap;box-shadow:4px 0 8px -2px rgba(0,0,0,0.4)';
  const thead = document.createElement('thead');
  const thr = document.createElement('tr');
  const th0 = document.createElement('th'); th0.style.cssText = 'padding:12px 16px;text-align:left;font-size:13px;color:var(--tx3);background:var(--raised);z-index:2;border-bottom:2px solid var(--border);border-right:2px solid var(--border);' + stickyBase; th0.textContent = '';
  thr.appendChild(th0);
  MSH.forEach((m, i) => { const th = document.createElement('th'); th.textContent = m; th.style.cssText = 'padding:12px 14px;font-size:13px;text-align:right;color:var(--tx3);text-transform:uppercase;letter-spacing:.5px;white-space:nowrap;background:var(--raised);border-bottom:2px solid var(--border)' + (i < 11 ? ';border-right:1px solid var(--border-lt)' : ''); thr.appendChild(th); });
  thead.appendChild(thr); tbl.appendChild(thead);

  const tbody = document.createElement('tbody');
  tableRows.forEach(r => {
    if (r.type === 'gap') {
      const tr = document.createElement('tr');
      const tdS = document.createElement('td'); tdS.style.cssText = 'height:8px;background:var(--raised);border-right:2px solid var(--border);' + stickyBase;
      const tdR = document.createElement('td'); tdR.colSpan = 12; tdR.style.height = '8px';
      tr.appendChild(tdS); tr.appendChild(tdR); tbody.appendChild(tr); return;
    }
    if (r.type === 'sec') {
      const tr = document.createElement('tr');
      const tdS = document.createElement('td'); tdS.style.cssText = 'padding:12px 16px;font-weight:700;font-size:16px;color:' + r.color + ';background:var(--surface);border-right:2px solid var(--border);' + stickyBase;
      tdS.textContent = r.label;
      const tdR = document.createElement('td'); tdR.colSpan = 12; tdR.style.cssText = 'background:var(--surface)';
      tr.appendChild(tdS); tr.appendChild(tdR); tbody.appendChild(tr); return;
    }

    const tr = document.createElement('tr');
    const bg = (r.type === 'tot' || r.type === 'der') ? 'var(--surface)' : 'var(--raised)';
    const tdL = document.createElement('td'); tdL.style.cssText = 'padding:10px 16px;font-size:14px;background:' + bg + ';border-right:2px solid var(--border);border-bottom:1px solid var(--border-lt);' + stickyBase;
    if (r.type === 'row') { tdL.style.paddingLeft = '28px'; tdL.style.color = 'var(--tx)'; }
    if (r.type === 'tot' || r.type === 'der') tdL.style.fontWeight = '700';
    if (r.type === 'tot') tdL.style.color = r.color;
    if (r.type === 'pct') { tdL.style.fontStyle = 'italic'; tdL.style.color = 'var(--tx3)'; }
    tdL.textContent = r.label; tr.appendChild(tdL);

    for (let m = 0; m < 12; m++) {
      const td = document.createElement('td'); td.style.cssText = 'padding:10px 14px;text-align:right;font-family:var(--mn);font-size:14px;white-space:nowrap;border-bottom:1px solid var(--border-lt)' + (m < 11 ? ';border-right:1px solid var(--border-lt)' : '');
      if (r.type === 'pct') {
        const pv = v[r.key][m];
        if (pv !== null) {
          td.textContent = (pv >= 0 ? '+' : '') + pv.toFixed(1) + '%';
          td.style.color = pv >= 0 ? 'var(--suc)' : 'var(--dng)';
        } else { td.textContent = '\u2014'; td.style.color = 'var(--tx3)'; }
        td.style.fontStyle = 'italic'; td.style.fontSize = '13px';
      } else {
        let val = r.type === 'row' ? v[r.key][m] * (r.sign || 1) : v[r.key][m];
        if (r.type === 'tot' || r.type === 'der') { td.style.fontWeight = '700'; td.style.background = 'var(--surface)'; td.style.borderTop = r.type === 'tot' ? '2px solid var(--border)' : 'none'; }
        if (r.type === 'tot') td.style.color = r.color;
        if (r.type === 'der') {
          const c = r.color || (val >= 0 ? 'var(--suc)' : 'var(--dng)');
          td.style.color = c; tdL.style.color = c;
        }
        td.textContent = fNum(val);
      }
      tr.appendChild(td);
    }
    tbody.appendChild(tr);
  });
  tbl.appendChild(tbody); card.appendChild(tbl); out.appendChild(card);
}

export function exportPl12Xlsx() {
  if (!lastPl12) { toast('Generišite P&L 12 Months prvo', 'err'); return; }
  const { year, kurs, monthsData } = lastPl12;
  const v = computePl12(monthsData);

  const xlRows = [
    { label: 'REVENUE', type: 'sec' },
    { label: '  Sales Revenue (60-61)', key: 'sales', sign: -1 },
    { label: '  Other Income (62-64, 66-68)', key: 'orev', sign: -1 },
    { label: 'Total Revenue', key: 'totalRev', type: 'tot' },
    { label: 'M/M Revenue Growth', key: 'revGrowth', type: 'pct' },
    { label: '', type: 'gap' },
    { label: 'COST OF GOODS SOLD', type: 'sec' },
    { label: '  Raw Materials Costs', key: 'rawMat', sign: 1 },
    { label: '  Manufacturing Costs', key: 'mfg', sign: 1 },
    { label: '  Direct Labor Costs', key: 'labor', sign: 1 },
    { label: '  Energy Costs', key: 'energy', sign: 1 },
    { label: '  Maintenance Costs', key: 'maint', sign: 1 },
    { label: '  Warehousing Costs', key: 'wareh', sign: 1 },
    { label: 'Cost of Goods Sold', key: 'totalCogs', type: 'tot' },
    { label: '', type: 'gap' },
    { label: 'GROSS MARGIN', key: 'gross', type: 'tot' },
    { label: 'Gross Margin %', key: 'grossPct', type: 'pct' },
    { label: '', type: 'gap' },
    { label: 'OPERATING EXPENSES', type: 'sec' },
    { label: '  Depreciation', key: 'dep', sign: 1 },
    { label: '  Selling, General & Admin', key: 'sga', sign: 1 },
    { label: 'Total OpEx', key: 'totalOpex', type: 'tot' },
    { label: '', type: 'gap' },
    { label: 'OPERATING INCOME (EBIT)', key: 'ebit', type: 'tot' },
    { label: 'Operating Income Margin %', key: 'ebitPct', type: 'pct' },
    { label: '', type: 'gap' },
    { label: '  Interest Income / (Expenses)', key: 'interestNet', sign: 1 },
    { label: '  Other Non-operating', key: 'otherNonOp', sign: -1 },
    { label: '', type: 'gap' },
    { label: 'NET INCOME', key: 'net', type: 'tot' },
    { label: 'Net Income Margin %', key: 'netPct', type: 'pct' },
  ];

  const rows = [];
  rows.push([xc('ECOR SERBIA — P&L 12 Months ' + year, { font: { bold: true, sz: 14 } }), ...MSH.map(() => xc('', {}))]);
  rows.push([xc('Kurs: ' + fNum(kurs), { font: { sz: 10 } }), ...MSH.map(() => xc('', {}))]);
  rows.push([xc('', XH), ...MSH.map(m => xc(m, XH))]);

  xlRows.forEach(r => {
    if (r.type === 'gap') { rows.push([xc('', {}), ...MSH.map(() => xc('', {}))]); return; }
    if (r.type === 'sec') { rows.push([xc(r.label, XS), ...MSH.map(() => xc('', XS))]); return; }
    const st = r.type === 'tot' ? XB : XN;
    if (r.type === 'pct') {
      const mVals = v[r.key].map(pv => xc(pv !== null ? pv / 100 : null, { font: { sz: 10, italic: true }, numFmt: '0.0%' }));
      rows.push([xc(r.label, { font: { italic: true } }), ...mVals]);
    } else {
      const mVals = v[r.key].map(val => xc(r.sign ? val * r.sign : val, st));
      rows.push([xc(r.label, r.type === 'tot' ? XB : {}), ...mVals]);
    }
  });

  const ws = XLSX.utils.aoa_to_sheet(rows.map(r => r.map(c => c.v)));
  ws['!cols'] = [{ wch: 28 }, ...MSH.map(() => ({ wch: 14 }))];
  applyXs(ws, rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'P&L 12 Months');
  XLSX.writeFile(wb, 'PL_12Months_' + year + '.xlsx', { bookSST: false, cellStyles: true });
  toast('Excel eksportovan');
  if (state.CU) sb.rpc('log_audit', { p_user_id: state.CU.id, p_action: 'export_excel', p_entity_type: 'mesecni', p_entity_name: 'excel_export' }).then(null, e => console.error('SBIS:', e));
}

// ============================================================
// INVENTORY 12 MONTHS
// ============================================================
let lastInv12 = null;

const INV12_CATS = [
  { key: 'mat', label: 'Materijal (101)', test: k => k.startsWith('101') && !k.startsWith('1012') && !k.startsWith('1013') },
  { key: 'waste', label: 'Otpadni materijal (1012)', test: k => k.startsWith('1012') },
  { key: 'fuel', label: 'Gorivo i mazivo (1013)', test: k => k.startsWith('1013') },
  { key: 'spare', label: 'Rezervni delovi (102)', test: k => k.startsWith('102') },
  { key: 'tools', label: 'Alat i sitan inventar (103)', test: k => k.startsWith('103') },
  { key: 'wip', label: 'Nedovršena proizvodnja (110)', test: k => k.startsWith('110') || k.startsWith('111') },
  { key: 'finished', label: 'Gotovi proizvodi (12)', test: k => k.startsWith('12') },
  { key: 'goods', label: 'Roba (13)', test: k => k.startsWith('13') },
  { key: 'advances', label: 'Plaćeni avansi (15)', test: k => k.startsWith('15') },
  { key: 'other', label: 'Ostalo (1xx)', test: k => k.startsWith('1') && !k.startsWith('101') && !k.startsWith('102') && !k.startsWith('103') && !k.startsWith('110') && !k.startsWith('111') && !k.startsWith('12') && !k.startsWith('13') && !k.startsWith('15') && !k.startsWith('1012') && !k.startsWith('1013') },
];

function computeInv12(monthsData) {
  const v = {};
  INV12_CATS.forEach(it => { v[it.key] = []; for (let m = 0; m < 12; m++) v[it.key].push(calcCat(monthsData[m], it.test)); });
  v.total = [];
  for (let m = 0; m < 12; m++) {
    v.total.push(INV12_CATS.reduce((s, it) => s + v[it.key][m], 0));
  }
  return v;
}

export async function generateInv12() {
  if (!state.CU) { toast('Niste prijavljeni', 'err'); return; }
  const year = parseInt(document.getElementById('inv12Year').value);
  const kurs = parseFloat(document.getElementById('inv12Kurs').value) || 108;

  showLoading();
  try {
    const promises = [];
    for (let m = 1; m <= 12; m++) {
      promises.push(sb.rpc('calculate_balance_sheet', { p_user_id: state.CU.id, p_year: year, p_month: m, p_kurs: kurs }));
    }
    const results = await Promise.all(promises);
    results.forEach((r, i) => { if (r.error) console.warn('Month ' + (i+1) + ' error:', r.error); });
    const monthsData = results.map(r => r.data || []);
    lastInv12 = { year, kurs, monthsData };
    renderInv12(monthsData, kurs, year);
    document.getElementById('inv12ExpBtn').style.display = '';
    toast('Inventory 12 Months generisan za ' + year);
  } catch (e) { toast('Greška: ' + (e.message || e), 'err'); }
  hideLoading();
}

function renderInv12(monthsData, kurs, year) {
  const out = document.getElementById('inv12Output'); out.textContent = '';
  const v = computeInv12(monthsData);

  const tableRows = [
    ...INV12_CATS.map(it => ({ type: 'row', label: it.label, key: it.key })),
    { type: 'gap' },
    { type: 'tot', label: 'TOTAL INVENTORY', key: 'total', color: 'var(--acc)' },
  ];

  const card = document.createElement('div'); card.className = 'card'; card.style.cssText = 'padding:0;overflow-x:auto';
  const hdr = document.createElement('div'); hdr.style.cssText = 'padding:16px 20px;border-bottom:2px solid var(--acc)';
  const h2 = document.createElement('h2'); h2.style.cssText = 'font-size:24px;color:var(--acc)'; h2.textContent = 'INVENTORY 12 MONTHS \u2014 ' + year;
  const sub2 = document.createElement('div'); sub2.style.cssText = 'font-size:13px;color:var(--tx3);margin-top:2px'; sub2.textContent = 'ECOR SERBIA \u00B7 Kurs: ' + fNum(kurs);
  hdr.appendChild(h2); hdr.appendChild(sub2); card.appendChild(hdr);

  const tbl = document.createElement('table'); tbl.style.cssText = 'border-collapse:separate;border-spacing:0;table-layout:auto;width:max-content;min-width:100%';
  const stickyBase = 'position:sticky;left:0;z-index:1;min-width:220px;white-space:nowrap;box-shadow:4px 0 8px -2px rgba(0,0,0,0.4)';
  const thead = document.createElement('thead');
  const thr = document.createElement('tr');
  const th0 = document.createElement('th'); th0.style.cssText = 'padding:12px 16px;text-align:left;font-size:13px;color:var(--tx3);background:var(--raised);z-index:2;border-bottom:2px solid var(--border);border-right:2px solid var(--border);' + stickyBase; th0.textContent = '';
  thr.appendChild(th0);
  MSH.forEach((m, i) => { const th = document.createElement('th'); th.textContent = m; th.style.cssText = 'padding:12px 14px;font-size:13px;text-align:right;color:var(--tx3);text-transform:uppercase;letter-spacing:.5px;white-space:nowrap;background:var(--raised);border-bottom:2px solid var(--border)' + (i < 11 ? ';border-right:1px solid var(--border-lt)' : ''); thr.appendChild(th); });
  thead.appendChild(thr); tbl.appendChild(thead);

  const tbody = document.createElement('tbody');
  tableRows.forEach(r => {
    if (r.type === 'gap') {
      const tr = document.createElement('tr');
      const tdS = document.createElement('td'); tdS.style.cssText = 'height:8px;background:var(--raised);border-right:2px solid var(--border);' + stickyBase;
      const tdR = document.createElement('td'); tdR.colSpan = 12; tdR.style.height = '8px';
      tr.appendChild(tdS); tr.appendChild(tdR); tbody.appendChild(tr); return;
    }
    const tr = document.createElement('tr');
    const isTot = r.type === 'tot';
    const bg = isTot ? 'var(--surface)' : 'var(--raised)';
    const tdL = document.createElement('td'); tdL.style.cssText = 'padding:10px 16px;font-size:14px;background:' + bg + ';border-right:2px solid var(--border);border-bottom:1px solid var(--border-lt);' + stickyBase;
    if (r.type === 'row') { tdL.style.paddingLeft = '16px'; tdL.style.color = 'var(--tx)'; }
    if (isTot) { tdL.style.fontWeight = '700'; tdL.style.color = r.color; }
    tdL.textContent = r.label; tr.appendChild(tdL);

    for (let m = 0; m < 12; m++) {
      const td = document.createElement('td'); td.style.cssText = 'padding:10px 14px;text-align:right;font-family:var(--mn);font-size:14px;white-space:nowrap;border-bottom:1px solid var(--border-lt)' + (m < 11 ? ';border-right:1px solid var(--border-lt)' : '');
      const val = v[r.key][m];
      if (isTot) { td.style.fontWeight = '700'; td.style.background = 'var(--surface)'; td.style.borderTop = '2px solid var(--border)'; td.style.color = r.color; }
      td.textContent = fNum(val);
      tr.appendChild(td);
    }
    tbody.appendChild(tr);
  });
  tbl.appendChild(tbody); card.appendChild(tbl); out.appendChild(card);
}

export function exportInv12Xlsx() {
  if (!lastInv12) { toast('Generišite Inventory 12 Months prvo', 'err'); return; }
  const { year, kurs, monthsData } = lastInv12;
  const v = computeInv12(monthsData);

  const xlRows = [
    ...INV12_CATS.map(it => ({ label: '  ' + it.label, key: it.key })),
    { label: '', type: 'gap' },
    { label: 'TOTAL INVENTORY', key: 'total', type: 'tot' },
  ];

  const rows = [];
  rows.push([xc('ECOR SERBIA — Inventory 12 Months ' + year, { font: { bold: true, sz: 14 } }), ...MSH.map(() => xc('', {}))]);
  rows.push([xc('Kurs: ' + fNum(kurs), { font: { sz: 10 } }), ...MSH.map(() => xc('', {}))]);
  rows.push([xc('', XH), ...MSH.map(m => xc(m, XH))]);

  xlRows.forEach(r => {
    if (r.type === 'gap') { rows.push([xc('', {}), ...MSH.map(() => xc('', {}))]); return; }
    const st = r.type === 'tot' ? XB : XN;
    const mVals = v[r.key].map(val => xc(val, st));
    rows.push([xc(r.label, r.type === 'tot' ? XB : {}), ...mVals]);
  });

  const ws = XLSX.utils.aoa_to_sheet(rows.map(r => r.map(c => c.v)));
  ws['!cols'] = [{ wch: 30 }, ...MSH.map(() => ({ wch: 14 }))];
  applyXs(ws, rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Inventory 12 Months');
  XLSX.writeFile(wb, 'Inventory_12Months_' + year + '.xlsx', { bookSST: false, cellStyles: true });
  toast('Excel eksportovan');
  if (state.CU) sb.rpc('log_audit', { p_user_id: state.CU.id, p_action: 'export_excel', p_entity_type: 'mesecni', p_entity_name: 'excel_export' }).then(null, e => console.error('SBIS:', e));
}
